import './coapplicantUnemploymentDetailsPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { toCurrencyString } from '@thesavingsgroup/utils/toCurrencyString';
import { toNumericString } from '@thesavingsgroup/utils/toNumericString';
import isEmpty from 'lodash/isEmpty';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../components/Alert/Alert';
import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { NumberInput } from '../../../components/NumberInput/NumberInput';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useLeadChannelStore from '../../../store/leadChannelStore/useLeadChannelStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const {
    computed: { phoneNumberWithCountryCode },
  } = useLeadChannelStore();
  const { next, previous } = useWorkflowStore();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentIncomeGrossAnnual,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentIncomeGrossAnnual,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      grossAnnualIncomeValue: toCurrencyString(
        coapplicantCurrentIncomeGrossAnnual,
      ),
    },
    delayError: 200,
    mode: 'onTouched',
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const grossAnnualIncomeValidation = {
    setValueAs: (value: string): string => toNumericString(value),
    required: t('validation.grossAnnualIncome.required'),
    min: {
      value: 0,
      message: t('validation.grossAnnualIncome.min', { min: '$0' }),
    },
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { grossAnnualIncomeValue } = values;

    if (!grossAnnualIncomeValue) {
      return;
    }

    setCoapplicantCurrentIncomeGrossAnnual(grossAnnualIncomeValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="page coapplicant-unemployment-details-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantUnemploymentDetails.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1
                data-testid={
                  locator.pages.coapplicantUnemploymentDetails.form.h1
                }
              >
                {t('CoapplicantUnemploymentDetailsPage.title')}
              </h1>
              <h2
                data-testid={
                  locator.pages.coapplicantUnemploymentDetails.form.h2
                }
              >
                {t('CoapplicantUnemploymentDetailsPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantUnemploymentDetails.alert.error
                    }
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <NumberInput
                    {...register(
                      'grossAnnualIncomeValue',
                      grossAnnualIncomeValidation,
                    )}
                    data-testid={
                      locator.pages.coapplicantUnemploymentDetails.form
                        .grossAnnualIncomeInput
                    }
                    error={errors.grossAnnualIncomeValue}
                    errorTestId={
                      locator.pages.coapplicantUnemploymentDetails.form
                        .grossAnnualIncomeInputError
                    }
                    label={t(
                      'CoapplicantUnemploymentDetailsPage.grossAnnualIncome',
                    )}
                    currency
                  />
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantUnemploymentDetails.form
                      .submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantUnemploymentDetailsPage.continue')}
                </Button>
              </div>

              <p
                className="form-disclosure"
                data-testid={locator.pages.coapplicantUnemploymentDetails.form.incomeDisclosure}
              >
                <Trans
                  components={{
                    phone: <a href={`tel:${phoneNumberWithCountryCode}`} />,
                  }}
                  i18nKey="disclosures.coapplicantIncome"
                  values={{ phoneNumber: phoneNumberWithCountryCode }}
                />
              </p>
            </div>
            <footer
              data-testid={
                locator.pages.coapplicantUnemploymentDetails.footer.root
              }
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantUnemploymentDetailsPage';
